import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconChevronLeft, IconPlus } from "@tabler/icons-react";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopCollectionCardSkeleton } from "../../../components/cards/DesktopCollectionCard";
import { Button } from "../../../components/ui/Button";
import { SaveableCollectionCard } from "../../../domains/playlist/components/SaveableCollectionCard";
import { useCreateCollection } from "../../../domains/playlist/hooks/useCreateCollection";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useApiQuery } from "../../../services/api";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventSelectItemPlaylist } from "../../../tools/analytics/domains/playlist.analytics";
import type { ProfileSubject } from "./DesktopProfilePage";

/**
 * Every collection a profile's owner created, three to a row, newest first
 * (Figma "05 - Profil", D3.profile.collections): the "See all" of the
 * profile's collections, as `/profile/playlist` is on mobile. The viewer's own
 * page carries the way to create one.
 */
export function DesktopProfileCollectionsPage({
  subject,
}: {
  subject: ProfileSubject;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();

  return (
    <div className="flex h-full flex-col overflow-hidden bg-ds-white">
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            back={{ kind: "none" }}
            trail={[]}
            current={t("Profile")}
          />
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-10 pt-8 pb-10">
          <div className="mx-auto flex max-w-desktop-block flex-col gap-6">
            <Link
              to={
                subject.kind === "me"
                  ? "/profile"
                  : `/profile/user/${subject.id}`
              }
              className="flex w-fit items-center gap-1.5 text-desktop-meta text-ds-slate-700 no-underline hover:text-ds-black"
            >
              <IconChevronLeft className="size-4" stroke={1.5} />
              {t("Profile")}
            </Link>
            {currentUser === null ? (
              <CollectionGridSkeleton />
            ) : (
              <Collections
                userId={subject.kind === "me" ? currentUser.id : subject.id}
                subject={subject}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Collections({
  userId,
  subject,
}: {
  userId: string;
  subject: ProfileSubject;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const createCollection = useCreateCollection(userId);
  const query = useApiQuery("/webapp/private/playlists/user/:userId", {
    param: { userId },
  });
  // Another member's page names them; the viewer's own page needs no lookup.
  const memberQuery = useApiQuery(
    "/webapp/private/users/:id",
    subject.kind === "member" ? { param: { id: subject.id } } : false
  );

  // A member's page waits for their name: its title is "Anna's collections".
  if (query.state === "pending" || memberQuery.state === "pending") {
    return <CollectionGridSkeleton />;
  }
  if (query.state === "error" || memberQuery.state === "error") {
    return (
      <p className="m-0 text-desktop-meta text-ds-strawberry-600">
        {t("Something went wrong. Please try again.")}
      </p>
    );
  }

  const title = (() => {
    switch (subject.kind) {
      case "me":
        return t("Collections I created");
      case "member": {
        if (memberQuery.state !== "success") {
          throw new Error("should not happen: the member is loaded");
        }
        const firstname = memberQuery.body.firstname;
        return t("{{name}}'s collections", {
          name: firstname === null ? t("First Name") : firstname,
        });
      }
      default:
        throw new Error(subject satisfies never);
    }
  })();

  const collections = query.body;
  return (
    <>
      <header className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="m-0 truncate text-desktop-h3 font-semibold text-ds-black">
            {title}
          </h1>
          <p className="m-0 text-desktop-meta text-ds-slate-700">
            {t("{{count}} collections", { count: collections.length })}
          </p>
        </div>
        {/* An empty grid's own tile is the way to create the first one. */}
        {subject.kind === "me" && collections.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              void createCollection(
                collections.map((collection) => collection.title)
              );
            }}
          >
            {t("Create a collection")}
          </Button>
        ) : null}
      </header>
      {collections.length === 0 ? (
        subject.kind === "me" ? (
          <div className="grid grid-cols-3 gap-8">
            <button
              type="button"
              onClick={() => {
                void createCollection([]);
              }}
              className="flex h-61 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-ds-slate-100 bg-ds-moss-50 text-sm text-ds-slate-700 transition-colors hover:border-ds-slate-300"
            >
              <IconPlus className="size-5" stroke={1.5} />
              {t("Create a collection")}
            </button>
          </div>
        ) : (
          <p className="m-0 text-desktop-meta text-ds-slate-600">
            {t("No playlist for this user")}
          </p>
        )
      ) : (
        <div className="grid grid-cols-3 gap-8">
          {collections.map((playlist) => (
            <SaveableCollectionCard
              key={playlist.id}
              playlist={playlist}
              listName={AnalyticsItemList.PROFILE}
              onOpen={() => {
                gaEventSelectItemPlaylist({
                  playlist,
                  listName: AnalyticsItemList.PROFILE,
                });
                history.push(`/profile/playlist/${playlist.id}`);
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}

function CollectionGridSkeleton(): React.JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-8">
      {[0, 1, 2].map((index) => (
        <DesktopCollectionCardSkeleton key={index} />
      ))}
    </div>
  );
}
